import { Component, OnDestroy, OnInit } from '@angular/core';
import { EventService } from 'src/app/service/event.service';
import { GeneralService } from 'src/app/service/general.service';
import { Modal } from '../controls/modal/modal.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { UserInfo } from 'src/app/model/User';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Utils } from 'src/app/utils';
import { ModalConfig } from '../controls/modal/modal-config';
import { ModalMessageComponent } from '../controls/modal-message/modal-message.component';
import { finalize, of, Subscription, tap } from 'rxjs';
import { Events, MsgPayLoad } from 'src/app/service/websocket.service';
import { DatabaseConfigRequest } from 'src/app/model/Setting';
import { TaskStatus } from 'src/app/model/TaskStatus';
import { Router } from '@angular/router';
import { SolutionInfo } from 'src/app/model/SolutionInfo';

//local validator
export function confirmPasswordValidator(group: FormGroup) {
  if(group.get('newPasswordCtrl').touched || group.get('confirmNewPasswordCtrl').touched){
    return group.get('newPasswordCtrl').value === group.get('confirmNewPasswordCtrl').value ? null : {passwordNotSame: true};
  }else{
    return null;
  }
}

@Component({
    selector: 'app-settings-database',
    templateUrl: './settings-database.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-database.component.css'],
    standalone: false
})
export class SettingsDatabaseComponent implements OnInit, OnDestroy {
  isSaving:boolean = false;
  isInitialized:boolean = false;
  currentUserInfo:UserInfo;
  savingBtn:string = $localize`Update`;
  solutionInfo:SolutionInfo;
  updatePasswordForm:FormGroup = new FormGroup({});
  isDBDefaultPwdChanged:boolean = false;
  solutionInfoSubscription: Subscription;

  constructor(private genService: GeneralService,
    private eventService: EventService,
    private modal: Modal,
    private authService: AuthenticationService,
    private router: Router
  ) { 
    this.currentUserInfo = this.authService.getLoggedInUser();;
  }

  ngOnInit(): void {
    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      if(Utils.isSuperuserRole(this.currentUserInfo)){
        this.isDBDefaultPwdChanged = this.solutionInfo.settings.isDefaultDBPwdChanged;
        if(!this.isDBDefaultPwdChanged){
          this.updatePasswordForm = new FormGroup({
            newPasswordCtrl: new FormControl('',
              [Validators.required,
                Validators.pattern(Utils.passwordRegex)
              ]
            ),
            confirmNewPasswordCtrl: new FormControl('',
              [Validators.required]
            ),
          });
        } else{
          this.updatePasswordForm = new FormGroup({
            currentPasswordCtrl: new FormControl('', [
              Validators.required, Validators.minLength(8)
            ]),
            newPasswordCtrl: new FormControl('',
              [Validators.required,
                Validators.pattern(Utils.passwordRegex)
              ]
            ),
            confirmNewPasswordCtrl: new FormControl('',
              [Validators.required]
            ),
          });
        }

        this.updatePasswordForm.setValidators(confirmPasswordValidator);
        this.isInitialized = true;
      }else{
        this.router.navigate(['/settings/general']);
      }
    });
  }

  ngOnDestroy(): void {
    this.solutionInfoSubscription?.unsubscribe();
  }

  get currentPasswordCtrl() { return this.updatePasswordForm.get('currentPasswordCtrl'); }
  get newPasswordCtrl() { return this.updatePasswordForm.get('newPasswordCtrl'); }
  get confirmNewPasswordCtrl() { return this.updatePasswordForm.get('confirmNewPasswordCtrl'); }

  update(): void {
    let msg:string = $localize`The database password will be changed to the new password. Do you want to update?<br/><b>Note: </b>To protect against remote brute force password attacks, the system doubles the delay after each unsuccessful login attempt, with minimum (0.25 seconds) and maximum (4 seconds) delays, and applies the same delay for correct passwords after a wrong attempt to prevent parallel attacks.`;

    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Update database password`,
          message: msg,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '550px',
        },
        id: 'otsc-settings-database-update'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;
          this.savingBtn = $localize`Updating...`;
          this.updatePasswordForm.disable();
          const dbPwd = !this.isDBDefaultPwdChanged ? "" : this.currentPasswordCtrl.value;
          const dbConfReq:DatabaseConfigRequest = new DatabaseConfigRequest(dbPwd, this.newPasswordCtrl.value);

          this.genService.updateDatabasePassword(dbConfReq).pipe(tap(resp => {
            let status:TaskStatus = resp.body;
            if(status.success && status.completed){
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`The database password was updated successfully.`);
              this.updatePasswordForm.reset();
              let msg = new MsgPayLoad(undefined, undefined);
              msg.setEvent(Events.RefreshSolution);
              this.eventService.broadcastEventMessage(msg);
            }else{
              Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`An error occurred while updating the database password. Please check the log for more details.`);
            }
          }),
            finalize(() => {
              this.isSaving = false;
              this.savingBtn = $localize`Update`;
              this.updatePasswordForm.enable();
            }),
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to reset?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-database-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.updatePasswordForm.reset();
        }
        sub.unsubscribe();
      });
  }

  canDeactivate() : boolean{
    return !this.updatePasswordForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-database-unsaved-changes');
  }
}